import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_TYPE_VALUES,
  CALL_OUTCOME_LABELS,
  CALL_OUTCOME_VALUES,
  TASK_PRIORITY_VALUES,
  type ActivityType,
  type CallOutcome,
  type TaskPriority,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from './Activities.module.css';

type Props = {
  projectId: string;
  leadId?: string;
  dealId?: string;
  accountId?: string;
};

export function ActivityComposer({ projectId, leadId, dealId, accountId }: Props) {
  const qc = useQueryClient();
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [internalOnly, setInternalOnly] = useState(true);
  const [pinned, setPinned] = useState(false);
  const [callOutcome, setCallOutcome] = useState<CallOutcome | ''>('');
  const [duration, setDuration] = useState('');

  const create = useMutation({
    mutationFn: () => {
      const dueIso = dueAt ? new Date(dueAt).toISOString() : null;
      return apiFetch('/api/activities', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          lead_id: leadId ?? null,
          deal_id: dealId ?? null,
          account_id: accountId ?? null,
          activity_type: activityType,
          title: title.trim() || null,
          body: body.trim() || null,
          due_at: activityType === 'task' ? dueIso : null,
          priority,
          pinned,
          internal_only: internalOnly,
          call_outcome: callOutcome || null,
          duration_minutes: duration ? Number(duration) : null,
        }),
      });
    },
    onSuccess: () => {
      setTitle('');
      setBody('');
      setDueAt('');
      setDuration('');
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      void qc.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (activityType === 'task' && !dueAt) return;
    if (activityType === 'note' && !body.trim() && !title.trim()) return;
    create.mutate();
  }

  return (
    <form className={styles.composer} onSubmit={(e) => void submit(e)}>
      <div className={styles.typeTabs}>
        {ACTIVITY_TYPE_VALUES.map((t) => (
          <Button
            key={t}
            type="button"
            variant={activityType === t ? 'primary' : 'secondary'}
            onClick={() => setActivityType(t)}
          >
            {ACTIVITY_TYPE_LABELS[t]}
          </Button>
        ))}
      </div>
      {(activityType === 'task' || activityType === 'call' || activityType === 'meeting') && (
        <Field label="Titel">
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      )}
      <Field label={activityType === 'task' ? 'Beschrijving' : 'Inhoud'}>
        <TextArea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {activityType === 'task' ? (
        <>
          <Field label="Vervaldatum">
            <TextInput
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              required
            />
          </Field>
          <Field label="Prioriteit">
            <select
              className={leadStyles.filterInput}
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
            >
              {TASK_PRIORITY_VALUES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </>
      ) : null}
      {activityType === 'call' || activityType === 'meeting' ? (
        <>
          <Field label="Resultaat">
            <select
              className={leadStyles.filterInput}
              value={callOutcome}
              onChange={(e) => setCallOutcome(e.target.value as CallOutcome | '')}
            >
              <option value="">—</option>
              {CALL_OUTCOME_VALUES.map((c) => (
                <option key={c} value={c}>
                  {CALL_OUTCOME_LABELS[c]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Duur (min)">
            <TextInput
              type="number"
              min={0}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </Field>
        </>
      ) : null}
      <label className={styles.internalBadge}>
        <input
          type="checkbox"
          checked={internalOnly}
          onChange={(e) => setInternalOnly(e.target.checked)}
        />{' '}
        Alleen intern
      </label>
      {activityType === 'note' ? (
        <label className={styles.internalBadge}>
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />{' '}
          Vastpinnen
        </label>
      ) : null}
      <Button type="submit" disabled={create.isPending}>
        Opslaan
      </Button>
    </form>
  );
}
